import { Link } from 'react-router-dom'
import {
  ArrowRight,
  Check,
  BarChart3,
  Palette,
  Globe,
  Zap,
  HelpCircle,
  Info,
  Mail
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

const features = [
  {
    icon: Palette,
    title: 'Beautiful Landing Pages',
    description: 'Create stunning waitlist pages with our drag-and-drop builder. No coding required.',
  },
  {
    icon: BarChart3,
    title: 'Advanced Analytics',
    description: 'Track signups, conversion rates, and geographic data to optimize your campaigns.',
  },
  {
    icon: Globe,
    title: 'Custom Domains',
    description: 'Use your own domain for a professional look that builds trust with your audience.',
  },
  {
    icon: Zap,
    title: 'Powerful Integrations',
    description: 'Connect with Mailchimp, Slack, Teams, and more to streamline your workflow.',
  },
]

const pricing = [
  {
    name: 'Free',
    price: '$0',
    description: 'Perfect for trying out Waitlist Manager',
    features: [
      '1 waitlist',
      '10 signups',
      'Basic analytics',
      'Email support',
      'Standard templates',
    ],
    cta: 'Get Started Free',
    popular: false,
  },
  {
    name: 'Premium',
    price: '$9',
    description: 'Everything you need to grow',
    features: [
      'Unlimited waitlists',
      'Unlimited signups',
      'Advanced analytics',
      'Priority support',
      'Custom domains',
      'A/B testing',
      'All integrations',
    ],
    cta: 'Start Premium Trial',
    popular: true,
  },
]

export function LandingPage() {
  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 bg-primary rounded-md flex items-center justify-center text-primary-foreground font-bold">
              W
            </div>
            <span className="font-semibold text-lg">Waitlist Manager</span>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <a href="#features" className="text-muted-foreground hover:text-foreground">Features</a>
            <a href="#pricing" className="text-muted-foreground hover:text-foreground">Pricing</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost">Sign In</Button>
            </Link>
            <Link to="/register">
              <Button>Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-muted/30 to-background">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 max-w-4xl mx-auto">
            Build Beautiful Waitlists in{' '}
            <span className="text-primary">Minutes</span>
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Create stunning waitlist pages, collect signups, and track performance
            with our easy-to-use platform. No coding required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="w-full sm:w-auto">
                Start Free Trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" size="lg" className="w-full sm:w-auto">
                Sign In
              </Button>
            </Link>
          </div>
          <div className="mt-12 flex items-center justify-center gap-8 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-green-500" />
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-green-500" />
              <span>Quick and easy setup</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-green-500" />
              <span>Cancel anytime</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Everything you need to launch</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              From beautiful landing pages to detailed analytics, we've got you covered.
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {features.map((feature, index) => (
              <Card key={index} className="text-center">
                <CardHeader>
                  <div className="mx-auto mb-4 h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-center">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Highlights Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid gap-8 md:grid-cols-3 text-center">
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-primary mb-2">Free to Start</div>
                <p className="text-muted-foreground">Get started with a free tier - no credit card required</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-primary mb-2">Quick Setup</div>
                <p className="text-muted-foreground">Create your first waitlist in minutes</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-primary mb-2">Flexible Plans</div>
                <p className="text-muted-foreground">Upgrade when you're ready for more</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Simple, transparent pricing</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Choose the plan that's right for your business. No hidden fees.
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-2 max-w-4xl mx-auto">
            {pricing.map((plan, index) => (
              <Card key={index} className={plan.popular ? 'border-primary shadow-lg' : ''}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-2xl">{plan.name}</CardTitle>
                    {plan.popular && (
                      <span className="bg-primary text-primary-foreground px-3 py-1 text-sm rounded-full">
                        Most Popular
                      </span>
                    )}
                  </div>
                  <div className="text-4xl font-bold">
                    {plan.price}
                    <span className="text-lg font-normal text-muted-foreground">/month</span>
                  </div>
                  <CardDescription>{plan.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-500" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/register">
                    <Button
                      className="w-full"
                      variant={plan.popular ? 'default' : 'outline'}
                    >
                      {plan.cta}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to get started?</h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Create your first waitlist today and start building your audience.
          </p>
          <Link to="/register">
            <Button size="lg">
              Start Your Free Trial
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Templates Section */}
      <section id="templates" className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Beautiful Templates</h2>
            <p className="text-muted-foreground">
              Choose from our collection of professionally designed templates
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-3 max-w-5xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle>Startup Launch</CardTitle>
                <CardDescription>Perfect for new product launches</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="aspect-video bg-gradient-to-br from-blue-100 to-purple-100 rounded-md mb-4" />
                <p className="text-sm text-muted-foreground">
                  Modern design with conversion-optimized layout
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>SaaS Waitlist</CardTitle>
                <CardDescription>Ideal for software products</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="aspect-video bg-gradient-to-br from-green-100 to-blue-100 rounded-md mb-4" />
                <p className="text-sm text-muted-foreground">
                  Clean interface with feature highlights
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>App Preview</CardTitle>
                <CardDescription>Showcase your mobile app</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="aspect-video bg-gradient-to-br from-orange-100 to-red-100 rounded-md mb-4" />
                <p className="text-sm text-muted-foreground">
                  Mobile-first design with app store links
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section id="integrations" className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Powerful Integrations</h2>
            <p className="text-muted-foreground">
              Connect with your favorite tools to streamline your workflow
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-4 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="h-16 w-16 bg-muted rounded-lg flex items-center justify-center mx-auto mb-3">
                <Mail className="h-8 w-8" />
              </div>
              <h3 className="font-medium">Mailchimp</h3>
              <p className="text-sm text-muted-foreground mt-1">Email marketing</p>
            </div>
            <div className="text-center">
              <div className="h-16 w-16 bg-muted rounded-lg flex items-center justify-center mx-auto mb-3">
                <Mail className="h-8 w-8" />
              </div>
              <h3 className="font-medium">Slack</h3>
              <p className="text-sm text-muted-foreground mt-1">Team notifications</p>
            </div>
            <div className="text-center">
              <div className="h-16 w-16 bg-muted rounded-lg flex items-center justify-center mx-auto mb-3">
                <Mail className="h-8 w-8" />
              </div>
              <h3 className="font-medium">Zapier</h3>
              <p className="text-sm text-muted-foreground mt-1">Automation</p>
            </div>
            <div className="text-center">
              <div className="h-16 w-16 bg-muted rounded-lg flex items-center justify-center mx-auto mb-3">
                <Mail className="h-8 w-8" />
              </div>
              <h3 className="font-medium">Webhook</h3>
              <p className="text-sm text-muted-foreground mt-1">Custom integration</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">About Waitlist Manager</h2>
            <p className="text-muted-foreground">
              Building the future of product launches
            </p>
          </div>
          <div className="prose prose-gray max-w-none">
            <p className="text-muted-foreground mb-6">
              Founded in 2024 in California, Waitlist Manager was born from the frustration of
              launching products without proper audience validation. We believe every product
              deserves a successful launch, and that starts with building the right audience.
            </p>
            <div className="grid gap-4 md:grid-cols-3 mt-8">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold text-primary">Free to Start</div>
                  <p className="text-sm text-muted-foreground">No credit card required to get started</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold text-primary">Quick Setup</div>
                  <p className="text-sm text-muted-foreground">Create your first waitlist in minutes</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold text-primary">Flexible Plans</div>
                  <p className="text-sm text-muted-foreground">Upgrade when you're ready for more</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Help Center Section */}
      <section id="help" className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Help Center</h2>
            <p className="text-muted-foreground">
              Find answers to common questions
            </p>
          </div>
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How do I create my first waitlist?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  After signing up, click "Create Waitlist" from your dashboard. Choose a template,
                  customize your content, and publish. Quick and easy setup!
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Can I use my own domain?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Yes! Premium users can connect custom domains. We provide SSL certificates and
                  handle all the technical setup for you.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How do I export my signups?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  You can export your signup list as CSV from the waitlist dashboard. Premium users
                  also get API access for automated exports.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20">
        <div className="container mx-auto px-4 max-w-2xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Contact Us</h2>
            <p className="text-muted-foreground">
              We're here to help you succeed
            </p>
          </div>
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-6">
                <div>
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email Support
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    support@team-skynet.io<br />
                    We respond within 24 hours
                  </p>
                </div>
                <div>
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    <HelpCircle className="h-4 w-4" />
                    Sales Inquiries
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    sales@team-skynet.io<br />
                    For enterprise and custom plans
                  </p>
                </div>
                <div>
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    Office
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    California, United States<br />
                    Available Mon-Fri 9am-5pm PST
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-card">
        <div className="container mx-auto px-4 py-12">
          <div className="grid gap-8 md:grid-cols-4">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="h-8 w-8 bg-primary rounded-md flex items-center justify-center text-primary-foreground font-bold">
                  W
                </div>
                <span className="font-semibold">Waitlist Manager</span>
              </div>
              <p className="text-muted-foreground text-sm">
                The easiest way to build and manage waitlists for your product launches.
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#features" className="hover:text-foreground">Features</a></li>
                <li><a href="#pricing" className="hover:text-foreground">Pricing</a></li>
                <li><a href="#templates" className="hover:text-foreground">Templates</a></li>
                <li><a href="#integrations" className="hover:text-foreground">Integrations</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {/* TODO: Update to link to ./helpsite once built */}
                <li><a href="#help" className="hover:text-foreground">Help Center</a></li>
                <li><a href="#contact" className="hover:text-foreground">Contact Us</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#about" className="hover:text-foreground">About</a></li>
                <li><Link to="/privacy" className="hover:text-foreground">Privacy</Link></li>
                <li><Link to="/terms" className="hover:text-foreground">Terms</Link></li>
                <li><Link to="/refund-policy" className="hover:text-foreground">Refund Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t mt-8 pt-8 flex items-center justify-between text-sm text-muted-foreground">
            <p>&copy; 2024 Waitlist Manager. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
              <Link to="/terms" className="hover:text-foreground">Terms</Link>
              <Link to="/refund-policy" className="hover:text-foreground">Refund Policy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
